import { MAX_AUTO_SAVE_RETRY } from "constants/maxAutoSaveRetry";
import { useCallback, useState, useEffect, useRef } from "react";

export const INITIAL_RETRY = 0;
type AnyPromise = (...args: any[]) => any;
/**
 * call onChange fn immediately and debounced save function after certain timeout
 * @param retryFn call back to call when
 * @param [timeout=3000] timeout ms, default 3000ms
 */
export default function useRetryFn(
  retryFn: AnyPromise,
  maxRetry = MAX_AUTO_SAVE_RETRY,
) {
  const retryFnRef = useRef(retryFn);

  useEffect(() => {
    retryFnRef.current = retryFn;
  }, [retryFn]);

  // use a ref to hold the latest retryFn value
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const { current: timer } = timerRef;
    // clean up side effects before unmount
    return () => timer && clearTimeout(timer);
  }, [timerRef]);

  const [retryCount, setRetryCount] = useState(INITIAL_RETRY);

  const fnWithRetry = useCallback(
    (arg?: unknown) => {
      // every time invoked, should reset retry
      setRetryCount(0);

      return new Promise((resolve, reject) => {
        async function handleFn(retries = INITIAL_RETRY) {
          try {
            const res = await retryFnRef.current(arg);
            resolve(res);
            return;
          } catch (error) {
            if (retries >= maxRetry + INITIAL_RETRY - 1) {
              reject(new Error(error));
              return;
            }
            setRetryCount((s) => s + 1);
            handleFn(retries + 1);
            return;
          }
        }

        return handleFn();
      });
    },
    [maxRetry, retryFnRef],
  );

  return { retryCount, fnWithRetry };
}
